import { useMemo } from 'react';
import {
  TERMS,
  bandFor,
  bandValue,
  growth,
  scoreLabel,
  studentName,
  type Assessment,
  type Measure,
  type Score,
  type Student,
} from '@td/shared';
import { usePrivacy } from '../privacy/PrivacyContext';

/** BOY, MOY and EOY side by side for one measure, with the gain between. */
export function GrowthView({
  assessment,
  measure,
  students,
  scores,
}: {
  assessment: Assessment;
  measure: Measure;
  students: Student[];
  scores: Score[];
}) {
  const { privacy } = usePrivacy();

  const byStudentTerm = useMemo(() => {
    const map = new Map<string, Score>();
    for (const s of scores) {
      if (s.measureId !== measure.id || !s.term) continue;
      map.set(`${s.studentId}:${s.term}`, s);
    }
    return map;
  }, [scores, measure.id]);

  const gains = students
    .map(
      (s) =>
        growth(byStudentTerm.get(`${s.id}:BOY`), byStudentTerm.get(`${s.id}:EOY`)) ??
        growth(byStudentTerm.get(`${s.id}:BOY`), byStudentTerm.get(`${s.id}:MOY`)),
    )
    .filter((g): g is number => g != null);

  const median =
    gains.length === 0 ? null : [...gains].sort((a, b) => a - b)[Math.floor(gains.length / 2)]!;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline gap-3 text-sm">
        <h3 className="text-espresso">
          {assessment.name} · {measure.name}
        </h3>
        {median != null && (
          <p className="text-stone">
            Median growth so far:{' '}
            <span className={median >= 0 ? 'text-olive' : 'text-rust'}>
              {median > 0 ? '+' : ''}
              {median}
            </span>{' '}
            across {gains.length} student{gains.length === 1 ? '' : 's'}
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse overflow-hidden rounded-card bg-white shadow-soft print:shadow-none">
          <thead>
            <tr className="bg-linen text-xs font-normal text-walnut">
              <th className="border-b border-r border-sand px-2 py-2 text-left">Student</th>
              {TERMS.map((t) => (
                <th key={t} className="border-b border-r border-sand px-2 py-2 text-left">
                  {t}
                </th>
              ))}
              <th className="border-b border-sand px-2 py-2 text-left">Growth</th>
            </tr>
          </thead>

          <tbody>
            {students.map((student) => {
              const boy = byStudentTerm.get(`${student.id}:BOY`);
              const moy = byStudentTerm.get(`${student.id}:MOY`);
              const eoy = byStudentTerm.get(`${student.id}:EOY`);
              const gain = growth(boy, eoy) ?? growth(boy, moy);

              return (
                <tr key={student.id}>
                  <th className="border-b border-r border-sand px-2 py-1.5 text-left text-xs font-normal">
                    <span className="text-stone">{student.number ?? '—'}</span>{' '}
                    <span className="text-espresso">
                      {privacy
                        ? `${studentName(student)[0]}. ${student.lastName[0]}.`
                        : `${studentName(student)} ${student.lastName}`}
                    </span>
                  </th>

                  {[boy, moy, eoy].map((score, i) => {
                    const band = bandFor(bandValue(score, measure), measure.bands);
                    return (
                      <td
                        key={TERMS[i]}
                        className="border-b border-r border-sand px-2 py-1.5 text-sm"
                        style={
                          band
                            ? { backgroundColor: `${band.color}24`, color: band.color }
                            : undefined
                        }
                        title={band?.label}
                      >
                        {scoreLabel(score, measure) || <span className="text-stone">—</span>}
                      </td>
                    );
                  })}

                  <td className="border-b border-sand px-2 py-1.5 text-sm">
                    {gain == null ? (
                      <span className="text-stone">—</span>
                    ) : (
                      <span className={gain >= 0 ? 'text-olive' : 'text-rust'}>
                        {gain > 0 ? '+' : ''}
                        {gain}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[11px] text-stone print:hidden">
        Growth compares the beginning of the year with the latest term recorded. A child missing
        either end shows no growth rather than a number that would flatter the average.
      </p>
    </div>
  );
}
